import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RouterState } from 'connected-react-router';

import { BoardStatus, boardState, setStatus } from 'common/modules/app/board';
import { Master } from 'common/modules/app/master';
import { getGlossaryAll, GlossaryState } from 'common/modules/app/glossaries';
import { Glossary } from 'common/utils/glossary';
import {
  UserInformation,
  updateUser,
  UpdateKeyValue
} from 'common/modules/domain/user';
import {
  saveCookie,
  SasCookieKeys,
  loadCookie,
  VALUE_DUMMY_REGION
} from 'common/utils/cookie-utils';
import { AppState } from 'pages';
import { SelectLanguageBoard } from 'pages/board/components/SelectLanguageBoard';
import { FetchState } from 'common/modules/app/fetch';
import { GET_PLAN_LIST_ID } from 'common/modules/domain/plan';

/**
 * StoreのStateからComponentに渡すPropsへ
 */
const useStateProps = () => {
  const masterState = useSelector<AppState, Master>(
    (state) => state.app.master
  );
  const boardState = useSelector<AppState, boardState>(
    (state) => state.app.board
  );
  const userState = useSelector<AppState, UserInformation>(
    (state) => state.domain.user
  );
  const glState = useSelector<AppState, GlossaryState>(
    (state) => state.app.glossaries
  );
  const routerState = useSelector<AppState, RouterState>(
    (state) => state.app.router
  );
  const fetchState = useSelector<AppState, FetchState>(
    (state) => state.app.fetch
  );
  const fetchProcess = fetchState[GET_PLAN_LIST_ID];
  return {
    board: boardState,
    master: masterState,
    user: userState,
    glossary: Glossary.instance(glState),
    router: routerState,
    fetchProcess: fetchProcess,
    clickStatus: boardState.clickStatus
  };
};

type SelectedState = ReturnType<typeof useStateProps>;
/**
 * StoreのStateとActionをdispatchする関数からComponentに渡すPropsへ
 */
const useMargeProps = (state: SelectedState) => {
  const dispatch = useDispatch();
  const _board = state.board;
  const _master = state.master;
  const _user = state.user;
  /** SelectLanguageBoardの表示有無 */
  const _isDisplay =
    _board.status === BoardStatus.LANGUAGE_DISP ||
    _board.status === BoardStatus.LANGUAGE_FROM_FIRST_DISP;
  /** クローズボタンをクリックした際の処理 */
  const onClickClose = () => {
    dispatch(setStatus(BoardStatus.NOT_DISP));
  };
  /** 言語を選択した際の処理 */
  const onClickLanguage = (id: string) => {
    // 選択した言語をCookieに設定
    saveCookie(SasCookieKeys.KEY_LANGUAGE, id, 180);

    if (
      (!_user.region || !_user.country) &&
      _board.status === BoardStatus.LANGUAGE_FROM_FIRST_DISP
    ) {
      // 初回アクセス画面経由で表示した場合
      // (国・地域設定済で初回アクセス画面が表示されることはない)

      // 選択した言語をuserStateに設定
      dispatch(updateUser(UpdateKeyValue.KEY_LANGUAGE, id));
      // 再度初回アクセス画面を表示
      dispatch(setStatus(BoardStatus.FIRST_DISP));
      // グロッサリの再取得
      dispatch(getGlossaryAll());
    } else {
      // 初回アクセス画面以外から表示した場合
      const cookie = loadCookie();
      if (cookie[SasCookieKeys.KEY_REGION] === '') {
        // 国が未選択の場合は初回アクセス画面を表示しないようにダミーを設定
        saveCookie(SasCookieKeys.KEY_REGION, VALUE_DUMMY_REGION);
      }
      // 言語選択画面を閉じる
      dispatch(setStatus(BoardStatus.NOT_DISP));
      // 元々表示していた画面を再表示
      window.location.reload();
    }
  };
  /** 言語リストを作成 */
  const _languageList = [];
  for (const [key, value] of Object.entries(_master.language)) {
    const _select = _user.language === key ? true : false;
    _languageList.push({
      id: key,
      text: state.glossary.glx(value.glossary),
      select: _select,
      onClick: onClickLanguage
    });
  }
  return {
    isDisplay: _isDisplay,
    glossary: state.glossary,
    onClickClose: onClickClose,
    items: _languageList,
    selectedLanguage: _user.language
  };
};

/**
 * 言語選択画面全体のContainer Component
 */
export default function ContainerComponent() {
  const _props = { ...useStateProps(), ...useMargeProps(useStateProps()) };
  return <SelectLanguageBoard {..._props} />;
}
